"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@os/components/ui/button";
import { ChevronLeft, ChevronRight, X, Monitor, EyeOff, Palette, Maximize, Minimize } from "lucide-react";
import { markdownToHtml, parseSlideMeta } from "./SlideMarkdownUtils";
import { Popover, PopoverTrigger, PopoverContent } from "@os/components/ui/popover";
import { Slider } from "@os/components/ui/slider";
import { 
  Select, 
  SelectTrigger, 
  SelectValue, 
  SelectContent, 
  SelectItem 
} from "@os/components/ui/select";

interface MapleSlidePresenterProps {
  open: boolean;
  onClose: () => void;
  slidesMd: string[];
  initialIndex?: number;
  textColor?: string;
}

const MapleSlidePresenter: React.FC<MapleSlidePresenterProps> = ({
  open,
  onClose,
  slidesMd,
  initialIndex = 0,
  textColor = "#111111",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(initialIndex);
  const [blackout, setBlackout] = useState(false);
  const [htmlSlides, setHtmlSlides] = useState<string[]>([]);
  // NEW: per-slide meta for bg/text from markdown directives
  const [slideMetaList, setSlideMetaList] = useState<{ bg?: string; text?: string; bgImage?: string; align?: 'left' | 'center' | 'right' }[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  // NEW: presentation appearance state
  const [bgMode, setBgMode] = useState<"theme" | "black" | "white" | "custom">("theme");
  const [customBg, setCustomBg] = useState<string>("#101010");
  const [textMode, setTextMode] = useState<"theme" | "custom">("theme");
  const [customText, setCustomText] = useState<string>(textColor || "#111111");
  const [fontScale, setFontScale] = useState<number>(1); // 0.75–2.0

  // Prepare HTML once when slides change or presenter opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    Promise.all(
      slidesMd.map(async (md) => {
        const meta = parseSlideMeta(md);
        const html = await markdownToHtml(md);
        return { html, meta };
      })
    ).then((items) => {
      if (!cancelled) {
        setHtmlSlides(items.map((i) => i.html));
        setSlideMetaList(items.map((i) => ({ bg: i.meta.bg, text: i.meta.text, bgImage: (i.meta as any).bgImage, align: (i.meta as any).align })));
        setIndex(Math.min(Math.max(0, initialIndex), Math.max(0, items.length - 1)));
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [open, slidesMd, initialIndex]);

  const goNext = useCallback(() => {
    setIndex((i) => Math.min(i + 1, Math.max(0, slidesMd.length - 1)));
  }, [slidesMd.length]);

  const goPrev = useCallback(() => {
    setIndex((i) => Math.max(i - 1, 0));
  }, []);

  // Fullscreen: enter when presenter opens; track changes; provide toggle
  useEffect(() => {
    const handleFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleFsChange);
    if (open) {
      // Attempt to request fullscreen on the presenter container
      containerRef.current?.requestFullscreen?.();
    }
    return () => {
      document.removeEventListener("fullscreenchange", handleFsChange);
    };
  }, [open]);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      containerRef.current?.requestFullscreen?.();
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        // Exit fullscreen before closing presenter
        document.exitFullscreen?.();
        onClose();
      } else if (e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        if (!blackout) goNext();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (!blackout) goPrev();
      } else if (e.key.toLowerCase() === "b") {
        e.preventDefault();
        setBlackout((b) => !b);
      } else if (e.key.toLowerCase() === "f") {
        e.preventDefault();
        toggleFullscreen();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, goNext, goPrev, blackout, onClose, toggleFullscreen]);

  if (!open) return null;

  const computedBg = blackout
    ? "#000000"
    : bgMode === "theme"
      ? "hsl(var(--background))"
      : bgMode === "black"
        ? "#000000"
        : bgMode === "white"
          ? "#ffffff"
          : customBg;

  const computedText = textMode === "theme"
    ? "hsl(var(--foreground))"
    : customText;

  // NEW: derive per-slide overrides from markdown directives
  const slideBg = slideMetaList[index]?.bg || computedBg;
  const slideText = slideMetaList[index]?.text || computedText;
  const slideBgImage = slideMetaList[index]?.bgImage;
  const slideAlign = slideMetaList[index]?.align;

  return (
    <div className="fixed inset-0 z-[10000]" ref={containerRef}>
      {/* Background respects theme or custom */}
      <div
        className="absolute inset-0"
        style={{
          backgroundColor: slideBg,
          ...(slideBgImage
            ? { backgroundImage: `url(${slideBgImage})`, backgroundSize: "cover", backgroundPosition: "center" }
            : {}),
        }}
      />

      {/* Top bar */}
      <div className="absolute top-4 left-4 flex items-center gap-2 z-[10003]">
        <Monitor className="w-5 h-5 text-white drop-shadow" />
        <span className="text-white/90 font-semibold drop-shadow">Presenter Mode</span>
      </div>

      {/* Controls: Exit + Settings */}
      <div className="absolute top-4 right-4 flex items-center gap-2 z-[10003]">
        {/* Fullscreen toggle */}
        <Button
          variant="ghost"
          size="sm"
          className="text-white hover:bg-white/10"
          onClick={toggleFullscreen}
          aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          title={isFullscreen ? "Exit fullscreen (F)" : "Enter fullscreen (F)"}
        >
          {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
        </Button>

        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/10"
              aria-label="Presentation settings"
              title="Presentation settings"
            >
              <Palette className="w-5 h-5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="z-[10005] w-80">
            <div className="space-y-3">
              <div>
                <div className="text-xs text-muted-foreground mb-1">Background</div>
                <Select value={bgMode} onValueChange={(v) => setBgMode(v as any)}>
                  <SelectTrigger className="h-8">
                    <SelectValue placeholder="Background" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="theme">Theme (light/dark)</SelectItem>
                    <SelectItem value="black">Black</SelectItem>
                    <SelectItem value="white">White</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
                {bgMode === "custom" && (
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      type="color"
                      value={customBg}
                      onChange={(e) => setCustomBg(e.target.value)}
                      className="h-8 w-10 rounded-md border border-input bg-background"
                      aria-label="Custom background color"
                    />
                    <span className="text-xs text-muted-foreground">{customBg}</span>
                  </div>
                )}
              </div>

              <div>
                <div className="text-xs text-muted-foreground mb-1">Text color</div>
                <Select value={textMode} onValueChange={(v) => setTextMode(v as any)}>
                  <SelectTrigger className="h-8">
                    <SelectValue placeholder="Text color" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="theme">Theme foreground</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
                {textMode === "custom" && (
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      type="color"
                      value={customText}
                      onChange={(e) => setCustomText(e.target.value)}
                      className="h-8 w-10 rounded-md border border-input bg-background"
                      aria-label="Custom text color"
                    />
                    <span className="text-xs text-muted-foreground">{customText}</span>
                  </div>
                )}
              </div>

              <div>
                <div className="text-xs text-muted-foreground mb-1">Zoom</div>
                <Slider
                  value={[fontScale]}
                  onValueChange={(vals) => setFontScale(vals[0])}
                  min={0.75}
                  max={2}
                  step={0.05}
                />
                <div className="mt-1 text-xs text-muted-foreground">Zoom: {fontScale.toFixed(2)}×</div>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setBgMode("theme");
                    setTextMode("theme");
                    setCustomBg("#101010");
                    setCustomText("#111111");
                    setFontScale(1);
                  }}
                >
                  Reset
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <Button
          variant="ghost"
          size="sm"
          className="text-white hover:bg-white/10"
          onClick={() => {
            document.exitFullscreen?.();
            onClose();
          }}
          aria-label="Exit presenter"
        >
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* Slide Content */}
      <div className="absolute inset-0 flex items-center justify-center z-[10001] pointer-events-none">
        {blackout ? (
          <div className="text-white/70 text-sm">Blackout</div>
        ) : loading ? (
          <div className="text-muted-foreground">Preparing slides...</div>
        ) : (
          <div
            className="w-full h-full max-w-[1600px] max-h-[900px] p-10 overflow-auto pointer-events-auto"
            style={{ color: slideText, ...( { zoom: fontScale } as any ) }}
          >
            <div
              className="prose dark:prose-invert max-w-none text-xl leading-relaxed"
              dangerouslySetInnerHTML={{ __html: htmlSlides[index] || "" }}
            />
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[10003]">
        <div className="flex items-center gap-2 rounded-md bg-black/50 text-white px-4 py-2 backdrop-blur">
          <Button
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/10"
            onClick={goPrev}
            disabled={loading || blackout || index <= 0}
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Prev
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/10"
            onClick={goNext}
            disabled={loading || blackout || index >= slidesMd.length - 1}
          >
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
          <div className="mx-3 text-sm">
            {slidesMd.length > 0 ? `${index + 1} / ${slidesMd.length}` : "0 / 0"}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className={`text-white hover:bg-white/10 ${blackout ? "bg-white/10" : ""}`}
            onClick={() => setBlackout((b) => !b)}
            title="Toggle blackout (B)"
          >
            <EyeOff className="w-4 h-4 mr-1" /> Blackout
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/10"
            onClick={() => {
              document.exitFullscreen?.();
              onClose();
            }}
          >
            Exit
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MapleSlidePresenter;